import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useShopAuth, errText, shopApi } from "@/lib/shopAuth";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ShopLayout from "./ShopLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inp = "mt-1 bg-white/5 border-white/10 text-neutral-100";

export default function ShopAuth() {
  const { login: shopLogin, register, googleLogin } = useShopAuth();
  const { login: staffLogin } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("login"); // login | register | forgot
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    const email = f.email.trim();
    if (!email) return;
    setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/lookup", { email });
        if (data.type === "employee") {
          const u = await staffLogin(email, f.password);
          toast.success(`Witaj, ${u?.name || ""}`.trim());
          navigate("/");
        } else {
          await shopLogin(email, f.password);
          toast.success("Zalogowano");
          // Konto z przypisanym sklepem prywatnym (np. REDFOX) trafia od razu do swojego sklepu.
          // Konta bez przypisania — bez zmian: sklep ogólny EMPRO (/sklep/konto).
          let dest = "/sklep/konto";
          try {
            const { data: myStores } = await shopApi.get("/b2b/my/stores");
            if ((myStores || []).length) dest = "/sklep/moj-sklep";
          } catch (_) { /* brak dostępu/sklepu — zostaje sklep ogólny */ }
          navigate(dest);
        }
      } else if (mode === "register") {
        await register(email, f.password, f.name);
        toast.success("Konto utworzone — możesz się teraz zalogować.");
        setMode("login");
      } else {
        const { data } = await shopApi.post("/shop/auth/forgot-password", { email });
        toast.success(data.message);
        setMode("login");
      }
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "login" ? "Zaloguj się" : mode === "register" ? "Załóż konto" : "Reset hasła";

  return (
    <ShopLayout>
      <div className="mx-auto max-w-md px-4 py-14">
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight mb-6">{title}</h1>

        <Button data-testid="shop-google-btn" onClick={googleLogin} variant="outline" className="w-full h-11 gap-2 border-white/20 bg-white text-[#0D1117] hover:bg-neutral-200 font-semibold">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />Kontynuuj z Google
        </Button>
        <div className="my-5 flex items-center gap-3 text-xs text-neutral-500"><div className="h-px flex-1 bg-white/10" />lub e-mailem<div className="h-px flex-1 bg-white/10" /></div>

        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {mode === "register" && (
            <div>
              <Label className="text-xs">Imię i nazwisko</Label>
              <Input data-testid="auth-name" autoComplete="name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} />
            </div>
          )}
          <div>
            <Label className="text-xs">E-mail</Label>
            <Input data-testid="auth-email" type="email" autoComplete="username" value={f.email} onChange={(e) => set("email", e.target.value)} className={inp} placeholder="np. jan@firma.pl" />
          </div>
          {mode !== "forgot" && (
            <div>
              <Label className="text-xs">Hasło</Label>
              <div className="relative">
                <Input data-testid="auth-password" type={showPw ? "text" : "password"} autoComplete={mode === "register" ? "new-password" : "current-password"} value={f.password} onChange={(e) => set("password", e.target.value)} className={`${inp} pr-10`} />
                <button type="button" data-testid="auth-password-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Ukryj hasło" : "Pokaż hasło"} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-amber-400 transition-colors">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}
          <Button type="submit" data-testid="auth-submit" disabled={busy || !f.email.trim()} className="w-full h-11 bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-bold uppercase tracking-wider gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Zaloguj" : mode === "register" ? "Utwórz konto" : "Wyślij link"}
          </Button>
        </form>

        <div className="mt-5 text-sm text-neutral-400 space-y-1">
          {mode === "login" && <>
            <p>Nie masz konta? <button className="text-amber-400 underline" onClick={() => setMode("register")} data-testid="go-register">Załóż konto</button></p>
            <p>Zapomniałeś hasła? <button className="text-amber-400 underline" onClick={() => setMode("forgot")}>Zresetuj</button></p>
          </>}
          {mode !== "login" && <p>Masz już konto? <button className="text-amber-400 underline" onClick={() => setMode("login")} data-testid="go-login">Zaloguj się</button></p>}
        </div>
      </div>
    </ShopLayout>
  );
}
